import { Sparkles } from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { Box, FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { creditsRefusalMessage, isCreditsRefusal } from '../../../lib/creditsRefusal';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import { usePromptPreview, useSuggestCustomInstructions } from '../hooks';
import type { CustomInstructionsSource } from '../types';

const MAX_LENGTH = 5000;

/**
 * An exhausted credit wallet is the one failure here the user can act on — they
 * can upgrade or wait for the renewal. Everything else (our upstream down, the
 * org guard refusing) is not actionable, so it gets the generic copy.
 *
 * Matched on the backend's `code`, never on the bare status: this route is also
 * behind OrgAccessGuard, which answers ORG_INACTIVE and NOT_ORG_MEMBER with a
 * status of its own. Keying off the status would tell a user whose membership
 * was removed that their AI credits ran out.
 */
interface CustomInstructionsEditorProps {
  organizationId: string;
  agentType: string;
  initialValue: string;
  onSave: (instructions: string, source: CustomInstructionsSource) => void;
  /**
   * Every edit as it happens, before (and regardless of) Save.
   *
   * Callers that persist through a mutation want the explicit Save. Callers
   * that only stage the value locally — the onboarding wizard, whose own
   * "Apply setup" is the real commit — need the running value, or text typed
   * and never Saved is dropped on the floor without a word.
   */
  onDraftChange?: (instructions: string) => void;
  isSaving: boolean;
}

export function CustomInstructionsEditor({
  organizationId,
  agentType,
  initialValue,
  onSave,
  onDraftChange,
  isSaving,
}: Readonly<CustomInstructionsEditorProps>) {
  const { t, language } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const [source, setSource] = useState<CustomInstructionsSource>('manual');
  const [showPreview, setShowPreview] = useState(false);
  const [undoValue, setUndoValue] = useState<string | null>(null);
  const [undoSource, setUndoSource] = useState<CustomInstructionsSource>('manual');
  const [aiError, setAiError] = useState<string | null>(null);
  const hasChanges = value !== initialValue;

  const { data: preview, isLoading: isLoadingPreview } = usePromptPreview(
    organizationId,
    agentType,
    showPreview,
  );
  const suggestMutation = useSuggestCustomInstructions();

  useEffect(() => {
    setValue(initialValue);
    setSource('manual');
    setUndoValue(null);
    setUndoSource('manual');
    setAiError(null);
  }, [initialValue]);

  // Single funnel for every user-driven value change (typing, AI suggestion,
  // undo) so no path can update the box without telling the caller. The
  // initialValue sync effect above deliberately does NOT go through here — that
  // is the caller's own value coming back, not an edit.
  const updateValue = useCallback(
    (next: string, nextSource: CustomInstructionsSource) => {
      setValue(next);
      setSource(nextSource);
      onDraftChange?.(next);
    },
    [onDraftChange],
  );

  const handleSave = useCallback(() => {
    onSave(value, source);
  }, [value, source, onSave]);

  const togglePreview = useCallback(() => {
    setShowPreview((prev) => !prev);
  }, []);

  // Empty box => generate a fresh instruction; non-empty box => enrich the
  // existing draft in place. Generation is read-only (never writes
  // AgentConfiguration.customPrompts) — persistence only happens through the
  // existing Save button, so the AI button behaves identically in every
  // CustomInstructionsEditor call site.
  const isGenerateMode = value.trim().length === 0;
  const aiLabel = suggestMutation.isPending
    ? t('agents.customInstructions.aiGenerating')
    : isGenerateMode
      ? t('agents.customInstructions.aiGenerate')
      : t('agents.customInstructions.aiEnrich');

  const handleAiSuggest = useCallback(() => {
    setAiError(null);
    const previousValue = value;
    const previousSource = source;
    const nextSource: CustomInstructionsSource = isGenerateMode ? 'ai_generated' : 'ai_enriched';

    suggestMutation.mutate(
      { organizationId, agentType, currentText: value, outputLanguage: language },
      {
        onSuccess: (result) => {
          const suggestion = result.suggestion.slice(0, MAX_LENGTH);
          if (suggestion === previousValue) return;
          setUndoValue(previousValue);
          setUndoSource(previousSource);
          updateValue(suggestion, nextSource);
        },
        onError: (error) => {
          setAiError(
            isCreditsRefusal(error)
              ? creditsRefusalMessage(error, t, language)
              : t('agents.customInstructions.aiError'),
          );
        },
      },
    );
  }, [value, source, isGenerateMode, organizationId, agentType, language, suggestMutation, t]);

  const handleUndoAiSuggestion = useCallback(() => {
    if (undoValue === null) return;
    updateValue(undoValue, undoSource);
    setUndoValue(null);
  }, [undoValue, undoSource, updateValue]);

  return (
    <VStack spacing="sm">
      <HStack spacing="sm" align="center" justify="between">
        <Text variant="caption" color={colors.text.secondary} style={styles.label}>
          {t('agents.customInstructions.title')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary}>
          {value.length}/{MAX_LENGTH}
        </Text>
      </HStack>

      <Text variant="caption" color={colors.text.tertiary} style={styles.hint}>
        {t('agents.customInstructions.hint')}
      </Text>

      <Box borderRadius="md" style={styles.editorContainer}>
        <FormInput
          value={value}
          onChangeText={(text) => updateValue(text.slice(0, MAX_LENGTH), 'manual')}
          multiline
          placeholder={t('agents.customInstructions.placeholder')}
          style={styles.editor}
          textAlignVertical="top"
        />
      </Box>

      <HStack spacing="sm" justify="between" align="center">
        <HStack spacing="xs" align="center">
          <Button
            variant="ghost"
            size="sm"
            onPress={handleAiSuggest}
            loading={suggestMutation.isPending}
            accessibilityLabel={aiLabel}
            testID="custom-instructions-ai-button"
          >
            <HStack spacing="xxs" align="center">
              <Sparkles size={14} color={colors.primary[400]} strokeWidth={2} />
              <Text variant="caption" color={colors.primary[400]}>
                {aiLabel}
              </Text>
            </HStack>
          </Button>
          {undoValue !== null && (
            <Button
              variant="ghost"
              size="sm"
              onPress={handleUndoAiSuggestion}
              accessibilityLabel={t('agents.customInstructions.aiUndo')}
              testID="custom-instructions-ai-undo"
            >
              {t('agents.customInstructions.aiUndo')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onPress={togglePreview}
            testID="custom-instructions-preview-toggle"
          >
            {showPreview
              ? t('agents.customInstructions.hidePreview')
              : t('agents.customInstructions.showPreview')}
          </Button>
        </HStack>
        <Button
          variant="primary"
          size="sm"
          onPress={handleSave}
          disabled={!hasChanges || isSaving}
          testID="custom-instructions-save"
        >
          {isSaving ? t('agents.customInstructions.saving') : t('agents.customInstructions.save')}
        </Button>
      </HStack>

      {aiError && (
        <Text
          variant="caption"
          color={colors.status.error.main}
          testID="custom-instructions-ai-error"
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          {aiError}
        </Text>
      )}

      {showPreview && (
        <Box borderRadius="md" padding="sm" style={styles.previewContainer}>
          <Text variant="caption" color={colors.primary[400]} style={styles.previewLabel}>
            {t('agents.customInstructions.previewTitle')}
          </Text>
          <ScrollView
            style={styles.previewScroll}
            nestedScrollEnabled
            testID="custom-instructions-preview-scroll"
          >
            <Text
              variant="caption"
              color={colors.text.secondary}
              style={styles.previewText}
              selectable
              testID="custom-instructions-preview-text"
            >
              {isLoadingPreview
                ? t('agents.customInstructions.loadingPreview')
                : preview?.prompt || t('agents.customInstructions.noPreview')}
            </Text>
          </ScrollView>
        </Box>
      )}
    </VStack>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '600',
  },
  hint: {
    fontSize: tokens.fontSize.xs,
    lineHeight: 15,
  },
  editorContainer: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
  },
  editor: {
    minHeight: 120,
    maxHeight: 300,
    padding: tokens.spacing.md,
    color: colors.text.primary,
    fontSize: tokens.fontSize.smPlus,
    lineHeight: 18,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
  previewContainer: {
    backgroundColor: colors.primary.muted,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  previewScroll: {
    maxHeight: 300,
  },
  previewLabel: {
    fontWeight: '600',
    marginBottom: tokens.spacing.sm,
  },
  previewText: {
    fontSize: tokens.fontSize.xs,
    lineHeight: 16,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
});
